import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Search, Star, X } from "lucide-react";
import { wellnessApi } from "@/lib/api";
import { SAMPLE_THERAPISTS, normalizeConsultant } from "@/lib/therapists";

const FILTERS = ["All", "Therapists", "Psychologists", "Counsellors"];

export default function WellnessConsultants() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("All");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    wellnessApi.consultants()
      .then((res) => {
        const real = (res.data?.consultants || []).map(normalizeConsultant);
        setList(real.length > 0 ? real : SAMPLE_THERAPISTS);
      })
      .catch(() => setList(SAMPLE_THERAPISTS))
      .finally(() => setLoading(false));
  }, []);

  const filtered = list.filter((t) => {
    if (t.conditions && t.conditions.length) return false;
    if (filter === "Therapists" && t.category !== "Therapist") return false;
    if (filter === "Psychologists" && !(t.category === "Psychologist" || t.category === "Psychiatrist")) return false;
    if (filter === "Counsellors" && t.category !== "Counsellor") return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [t.full_name, t.title, ...(t.tags || []), ...(t.specialisations || [])].join(" ").toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4">
      <button onClick={() => navigate("/mental-wellness")} className="flex items-center gap-1 text-sm font-medium" style={{ color: "#7C3AED" }} data-testid="therapist-list-back">
        <ArrowLeft className="h-4 w-4" /> Mental Wellness
      </button>
      <div>
        <h1 className="text-2xl font-semibold" style={{ color: "#2E1065" }}>Talk to a Mental Health Professional</h1>
        <p className="text-muted-foreground mt-1">Certified therapists, psychologists and counsellors.</p>
      </div>

      <div className="flex items-center gap-2 rounded-2xl border bg-white px-3 py-2.5" style={{ borderColor: "#E7DEF8" }}>
        <Search className="h-4 w-4" style={{ color: "#8B7CA6" }} />
        <input className="flex-1 outline-none text-sm bg-transparent" placeholder="Search by problem, e.g. anxiety, sleep, relationships" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="consultant-search" />
        {query && <button onClick={() => setQuery("")} data-testid="consultant-search-clear"><X className="h-4 w-4" style={{ color: "#C9B9E8" }} /></button>}
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f.toLowerCase()}`} className="shrink-0 rounded-full px-4 py-2 text-sm font-semibold border" style={filter === f ? { background: "#7C3AED", borderColor: "#7C3AED", color: "#fff" } : { background: "#fff", borderColor: "#E7DEF8", color: "#6B5B8A" }}>{f}</button>
        ))}
      </div>

      {loading ? (
        <p className="text-center text-muted-foreground py-10">Loading professionals…</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12" data-testid="consultant-empty">
          <p className="font-semibold" style={{ color: "#2E1065" }}>No professionals match your search.</p>
          <p className="text-sm text-muted-foreground mt-1">Try a different concern or clear the search.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {filtered.map((t) => (
            <button key={t.id} onClick={() => navigate(`/mental-wellness/therapist/${t.id}`)} data-testid={`therapist-card-${t.id}`} className="rounded-2xl border bg-white p-4 text-left hover:shadow-md transition-all" style={{ borderColor: "#EDE7F8" }}>
              <div className="flex gap-3">
                <img src={t.profile_photo} alt={t.full_name} className="h-16 w-16 rounded-2xl object-cover" style={{ background: "#EDE7F8" }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-bold truncate" style={{ color: "#1F1147" }}>{t.full_name}</p>
                    <span className="flex items-center gap-1 text-xs font-semibold" style={{ color: "#6B5B8A" }}><Star className="h-3 w-3" style={{ color: "#F59E0B", fill: "#F59E0B" }} />{t.rating} ({t.reviews})</span>
                  </div>
                  <p className="text-sm font-semibold mt-0.5" style={{ color: "#7C3AED" }}>{t.title}</p>
                  <p className="text-xs" style={{ color: "#8B7CA6" }}>{t.experience_years} years experience</p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {t.tags.map((tag) => <span key={tag} className="text-xs font-semibold rounded-md px-2 py-0.5" style={{ background: "#F3EEFC", color: "#7C3AED" }}>{tag}</span>)}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t" style={{ borderColor: "#F1ECFA" }}>
                <span className="flex items-center gap-1.5 text-sm font-semibold" style={{ color: "#16A34A" }}><span className="h-2 w-2 rounded-full" style={{ background: "#16A34A" }} />{t.availability}</span>
                <span className="rounded-lg px-5 py-1.5 text-sm font-bold text-white" style={{ background: "#7C3AED" }}>Book</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
